import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, ShieldAlert } from "lucide-react";
import API from "@/lib/api";
import { CATEGORIES, STONES, categoryLabel, formatPrice, stoneLabel } from "@/lib/data";

const inputCls =
  "w-full rounded-lg border border-line bg-surface px-4 py-3 text-sm text-paper placeholder:text-mist/50 outline-none transition-colors duration-300 focus:border-gold/60";

const labelCls = "text-[11px] uppercase tracking-widest2 text-mist";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
  </svg>
);

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await API.post("/auth/login", { email, password });
      window.location.reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="admin-login-view">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 md:p-10 space-y-6"
      >
        <div>
          <p className="font-display text-3xl text-paper">Keeper&rsquo;s Desk</p>
          <p className="mt-2 text-sm text-mist">Sign in to manage the cabinet.</p>
        </div>

        <button
          type="button"
          onClick={signInWithGoogle}
          data-testid="admin-google-signin-button"
          className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-line bg-ink px-6 py-3.5 text-[13px] font-semibold uppercase tracking-widest2 text-paper transition-colors duration-300 hover:border-gold/60 hover:text-gold"
        >
          <GoogleMark /> Continue with Google
        </button>

        <div className="flex items-center gap-4">
          <span className="h-px flex-1 bg-line" />
          <span className="text-[11px] uppercase tracking-widest2 text-mist/60">or with email</span>
          <span className="h-px flex-1 bg-line" />
        </div>

        <div>
          <label htmlFor="admin-email" className={labelCls}>Email</label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`mt-2 ${inputCls}`}
            placeholder="Your email"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className={labelCls}>Password</label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`mt-2 ${inputCls}`}
            placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="text-sm text-wine border border-wine/40 rounded-lg px-4 py-3">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="w-full rounded-full bg-gold px-6 py-3.5 text-[13px] font-semibold uppercase tracking-widest2 text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60"
        >
          {busy ? "Turning the key…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function AccessDenied({ user }) {
  const logout = async () => {
    await API.post("/auth/logout");
    window.location.reload();
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="admin-access-denied">
      <div className="w-full max-w-sm rounded-2xl border border-wine/40 bg-surface p-8 md:p-10 text-center">
        <ShieldAlert size={28} className="mx-auto text-wine" />
        <p className="mt-5 font-display text-2xl text-paper">Not the keeper&rsquo;s key</p>
        <p className="mt-3 text-sm text-mist leading-relaxed">
          Signed in as <span className="text-paper">{user.email}</span>, but this Google account
          isn&rsquo;t on the keeper&rsquo;s list.
        </p>
        <button
          onClick={logout}
          data-testid="admin-access-denied-logout"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full border border-line px-6 py-3 text-[12px] uppercase tracking-widest2 text-mist transition-colors duration-300 hover:border-wine hover:text-wine"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

function Desk({ user }) {
  const empty = { name: "", category: CATEGORIES[0].key, stone: "", price: "", stock: "", image_url: "", description: "", featured: false };
  const [form, setForm] = useState(empty);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [queries, setQueries] = useState([]);

  const load = () => {
    API.get("/products").then((r) => setProducts(r.data));
    API.get("/queries").then((r) => setQueries(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/products", {
        ...form,
        stone: form.stone || null,
        price: parseFloat(form.price),
        stock: parseInt(form.stock || "0", 10),
      });
      toast.success(`"${form.name}" is on the shelf.`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    try {
      await API.delete(`/products/${id}`);
      toast.success(`"${name}" left the cabinet.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await API.post("/auth/logout");
    window.location.reload();
  };

  return (
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12" data-testid="admin-desk-view">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[12px] uppercase tracking-widest2 text-gold">Keeper&rsquo;s Desk</p>
          <h1 className="mt-2 font-display font-light tracking-tighter text-4xl md:text-5xl">
            The cabinet, <span className="italic text-gold">at a glance</span>
          </h1>
          <p className="mt-2 text-xs text-mist/70" data-testid="admin-signed-in-as">
            Signed in as {user.email}
          </p>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-[12px] uppercase tracking-widest2 text-mist transition-colors duration-300 hover:border-wine hover:text-wine"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <form onSubmit={submit} data-testid="admin-product-form" className="lg:col-span-4 rounded-2xl border border-line bg-surface p-7 space-y-5 h-fit">
          <p className="font-display text-2xl text-paper flex items-center gap-2">
            <Plus size={18} className="text-gold" /> Add a piece
          </p>
          <div>
            <label htmlFor="product-name" className={labelCls}>Name</label>
            <input id="product-name" data-testid="admin-product-name-input" required value={form.name} onChange={set("name")} className={`mt-2 ${inputCls}`} placeholder="Amethyst spire" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="product-category" className={labelCls}>Category</label>
              <select id="product-category" data-testid="admin-product-category-select" value={form.category} onChange={set("category")} className={`mt-2 ${inputCls}`}>
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key} className="bg-surface">{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="product-price" className={labelCls}>Price (₹)</label>
              <input id="product-price" data-testid="admin-product-price-input" required type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className={`mt-2 ${inputCls}`} placeholder="42" />
            </div>
          </div>
          <div>
            <label htmlFor="product-stock" className={labelCls}>Stock</label>
            <input id="product-stock" data-testid="admin-product-stock-input" type="number" min="0" value={form.stock} onChange={set("stock")} className={`mt-2 ${inputCls}`} placeholder="3" />
          </div>
          <div>
            <label htmlFor="product-stone" className={labelCls}>Stone (optional)</label>
            <select id="product-stone" data-testid="admin-product-stone-select" value={form.stone} onChange={set("stone")} className={`mt-2 ${inputCls}`}>
              <option value="" className="bg-surface">No stone</option>
              {STONES.map((s) => (
                <option key={s.key} value={s.key} className="bg-surface">{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="product-image" className={labelCls}>Image URL</label>
            <input id="product-image" data-testid="admin-product-image-input" type="url" value={form.image_url} onChange={set("image_url")} className={`mt-2 ${inputCls}`} placeholder="https://…" />
          </div>
          <div>
            <label htmlFor="product-description" className={labelCls}>Description</label>
            <textarea id="product-description" data-testid="admin-product-description-textarea" rows={3} value={form.description} onChange={set("description")} className={`mt-2 resize-none ${inputCls}`} placeholder="Where it came from, how it feels…" />
          </div>
          <label className="flex items-center gap-3 text-sm text-mist cursor-pointer" data-testid="admin-product-featured-label">
            <input type="checkbox" data-testid="admin-product-featured-checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#bfa15f]" />
            Feature on the home page
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-product-submit"
            className="w-full rounded-full bg-gold px-6 py-3.5 text-[13px] font-semibold uppercase tracking-widest2 text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60"
          >
            {busy ? "Placing on the shelf…" : "Add to the cabinet"}
          </button>
        </form>

        <div className="lg:col-span-8" data-testid="admin-product-list">
          <div className="rounded-2xl border border-line bg-surface overflow-hidden">
            <div className="px-6 py-4 border-b border-line text-[11px] uppercase tracking-widest2 text-mist">
              {products.length} pieces in the cabinet
            </div>
            {products.length === 0 ? (
              <p className="px-6 py-14 text-center font-display italic text-2xl text-mist">
                Nothing yet. Add your first piece.
              </p>
            ) : (
              <ul className="divide-y divide-[#27272a]">
                {products.map((p) => (
                  <li key={p.id} data-testid={`admin-product-row-${p.id}`} className="px-6 py-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors duration-200">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="h-12 w-12 rounded-lg object-cover border border-line" />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-coal border border-line" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-paper text-sm truncate">{p.name} {p.featured && <span className="text-gold text-[11px] uppercase tracking-widest2 ml-2">featured</span>}</p>
                      <p className="text-mist text-xs mt-0.5">{categoryLabel(p.category)} &middot; {p.stock} in stock</p>
                    </div>
                    <p className="font-display text-lg text-gold whitespace-nowrap">{formatPrice(p.price)}</p>
                    <button
                      onClick={() => remove(p.id, p.name)}
                      data-testid={`admin-product-delete-${p.id}`}
                      aria-label={`Delete ${p.name}`}
                      className="p-2 rounded-lg text-mist/60 transition-colors duration-200 hover:text-wine hover:bg-wine/10"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10" data-testid="admin-queries-list">
        <div className="rounded-2xl border border-line bg-surface overflow-hidden">
          <div className="px-6 py-4 border-b border-line text-[11px] uppercase tracking-widest2 text-mist">
            {queries.length} {queries.length === 1 ? "query" : "queries"} from visitors
          </div>
          {queries.length === 0 ? (
            <p className="px-6 py-10 text-center font-display italic text-xl text-mist">No queries yet.</p>
          ) : (
            <ul className="divide-y divide-[#27272a]">
              {queries.map((q, i) => (
                <li key={`${q.email}-${q.created_at}-${i}`} data-testid={`admin-query-row-${i}`} className="px-6 py-4">
                  <p className="text-paper text-sm">
                    {q.name} <span className="text-mist/60">&middot; {q.email}</span>
                  </p>
                  <p className="text-mist text-sm mt-1 leading-relaxed">{q.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const location = useLocation();
  const [checking, setChecking] = useState(!location.state?.user);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;
    if (window.location.hash?.includes("session_id=")) return;
    API.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [location.state]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16" data-testid="admin-checking">
        <p className="font-display italic text-2xl text-mist">Unlocking&hellip;</p>
      </div>
    );
  }

  return (
    <main className="pt-16 min-h-screen" data-testid="admin-page">
      {!user ? <Login /> : user.role === "admin" ? <Desk user={user} /> : <AccessDenied user={user} />}
    </main>
  );
}
